import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import * as Select from '@radix-ui/react-select';
import { Check, ChevronDown, Copy, Link2, Trash2, X } from 'lucide-react';
import { shareLinksApi, type CreateShareLinkParams } from '../api/share-links';
import { toast } from './Toast';
import { formatRelativeTime } from '../lib/utils';

const PERMISSION_LABEL: Record<string, string> = {
  view: '只读',
  comment: '可评论',
  edit: '可编辑',
};

interface ShareLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  wsId: string;
  docId: string;
}

export function ShareLinkDialog({ open, onOpenChange, wsId, docId }: ShareLinkDialogProps) {
  const queryClient = useQueryClient();

  const { data: links = [], isLoading } = useQuery({
    queryKey: ['share-links', wsId, docId],
    queryFn: () => shareLinksApi.list(wsId, docId),
    enabled: open && !!wsId && !!docId,
  });

  const [permission, setPermission] = useState<CreateShareLinkParams['permission']>('view');
  const [password, setPassword] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const createMutation = useMutation({
    mutationFn: () =>
      shareLinksApi.create(wsId, docId, {
        permission,
        password: password || undefined,
        expiresAt: expiresAt || undefined,
      }),
    onSuccess: (link) => {
      queryClient.invalidateQueries({ queryKey: ['share-links', wsId, docId] });
      toast.success('分享链接已创建');
      setPermission('view');
      setPassword('');
      setExpiresAt('');
      copyToClipboard(fullUrl(link.url));
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : '创建分享链接失败');
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (linkId: string) => shareLinksApi.revoke(wsId, linkId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['share-links', wsId, docId] });
      toast.success('分享链接已撤销');
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : '撤销失败');
    },
  });

  /** 将后端返回的相对路径拼接为完整 URL */
  function fullUrl(path: string) {
    if (path.startsWith('http')) return path;
    return `${window.location.origin}${path}`;
  }

  async function copyToClipboard(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('链接已复制到剪贴板');
    } catch {
      toast.error('复制失败，请手动复制');
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-bg-overlay backdrop-blur-sm animate-fade-in" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[480px] max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-bg-surface p-6 shadow-xl ring-1 ring-black/5"
          aria-describedby={undefined}
        >
          <div className="flex items-start justify-between">
            <Dialog.Title className="text-lg font-semibold">分享文档</Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                className="flex h-6 w-6 items-center justify-center rounded text-text-tertiary hover:bg-bg-subtle"
                aria-label="关闭"
              >
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          {/* 已有链接列表 */}
          <div className="mt-5">
            <h3 className="text-sm font-medium text-text-secondary">已有分享链接</h3>
            {isLoading ? (
              <div className="mt-2 space-y-2">
                {[1, 2].map((i) => (
                  <div key={i} className="h-12 animate-shimmer rounded-lg bg-bg-subtle" />
                ))}
              </div>
            ) : links.length === 0 ? (
              <p className="mt-2 text-sm text-text-tertiary">暂无分享链接</p>
            ) : (
              <div className="mt-2 space-y-2">
                {links.map((link) => (
                  <div
                    key={link.id}
                    className="flex items-center gap-3 rounded-lg border border-border bg-bg-subtle p-3"
                  >
                    <Link2 className="h-4 w-4 shrink-0 text-text-tertiary" />
                    <div className="flex-1 overflow-hidden">
                      <div className="flex items-center gap-2">
                        <span className="tag tag-default text-xs">
                          {PERMISSION_LABEL[link.permission] || link.permission}
                        </span>
                        {link.expiresAt && (
                          <span className="text-xs text-text-tertiary">
                            到期 {formatRelativeTime(link.expiresAt)}
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 truncate text-xs text-text-tertiary">
                        {fullUrl(link.url)}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="flex h-7 w-7 items-center justify-center rounded text-text-tertiary hover:bg-bg-surface hover:text-text-primary"
                      onClick={() => copyToClipboard(fullUrl(link.url))}
                      aria-label="复制链接"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      className="flex h-7 w-7 items-center justify-center rounded text-text-tertiary hover:bg-[#fef2f2] hover:text-danger"
                      onClick={() => {
                        if (confirm('确定撤销此分享链接？')) {
                          revokeMutation.mutate(link.id);
                        }
                      }}
                      aria-label="撤销链接"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 创建新链接 */}
          <div className="mt-6 border-t border-border pt-5">
            <h3 className="text-sm font-medium">创建新分享链接</h3>
            <div className="mt-3 space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="share-permission">
                  权限
                </label>
                <Select.Root
                  value={permission}
                  onValueChange={(v) => setPermission(v as CreateShareLinkParams['permission'])}
                >
                  <Select.Trigger id="share-permission" className="input flex items-center justify-between">
                    <Select.Value>{PERMISSION_LABEL[permission]}</Select.Value>
                    <Select.Icon>
                      <ChevronDown className="h-3.5 w-3.5 text-text-tertiary" />
                    </Select.Icon>
                  </Select.Trigger>
                  <Select.Portal>
                    <Select.Content
                      className="z-50 min-w-[160px] overflow-hidden rounded-md border border-border bg-bg-surface shadow-md"
                      position="popper"
                      sideOffset={4}
                    >
                      <Select.Viewport className="p-1">
                        {(['view', 'comment', 'edit'] as const).map((r) => (
                          <Select.Item
                            key={r}
                            value={r}
                            className="flex cursor-pointer items-center justify-between rounded px-2 py-1.5 text-sm text-text-primary outline-none data-[highlighted]:bg-bg-subtle"
                          >
                            <Select.ItemText>{PERMISSION_LABEL[r]}</Select.ItemText>
                            <Select.ItemIndicator>
                              <Check className="h-3.5 w-3.5 text-accent" />
                            </Select.ItemIndicator>
                          </Select.Item>
                        ))}
                      </Select.Viewport>
                    </Select.Content>
                  </Select.Portal>
                </Select.Root>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="share-password">
                  密码（可选）
                </label>
                <input
                  id="share-password"
                  type="text"
                  className="input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="留空则不设密码"
                />
                <p className="mt-1 text-xs text-text-tertiary">
                  不设密码则任何人可通过链接直接查看文档内容
                </p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="share-expires">
                  过期时间（可选）
                </label>
                <input
                  id="share-expires"
                  type="datetime-local"
                  className="input"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                disabled={createMutation.isPending}
                onClick={() => createMutation.mutate()}
                className="btn btn-primary btn-md"
              >
                {createMutation.isPending ? '创建中…' : '创建链接'}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}